import { ColDef, ICellRendererParams } from 'ag-grid-community';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useUtilityVehiclesQuery } from '../../../hooks/queries/useUtilityVehiclesQuery';
import { useAuth } from '../../../hooks/useAuth';
import { UtilityVehicle } from '../../../services/apiSLTP/materials/utilityVehicles';
import { capitalizeFirstLetter, formatName } from '../../../utils/capitalize';
import { getPriceTypeInfos, localStorageKeys, permissionLevels, systemConfigKeyEnum } from '../../../utils/enums';
import { formatNumber } from '../../../utils/numbers';
import { InheritedPriceDisplay } from '../../InheritedPriceDisplay';
import LicensePlate from '../../LicensePlate';
import GenericAgGridTable from '../GenericAgGridTable';
import { useSystemConfig } from '../../../hooks/useSystemConfig';

const VehiclesPLITable: React.FC = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { getConfigValue } = useSystemConfig();

    const { data: vehicles = [], isLoading } = useUtilityVehiclesQuery({ queries: { active: true } });

    const rowData = vehicles.sort((a, b) => {
        const typeA = a.vehicleTypeId?.displayOrder || 0;
        const typeB = b.vehicleTypeId?.displayOrder || 0;
        if (typeA !== typeB) return typeA - typeB;
        return (a?.designation || '').localeCompare(b?.designation || '');
    });

    const columnDefs: ColDef<UtilityVehicle>[] = [
        {
            headerName: capitalizeFirstLetter(t('name')),
            field: 'designation',
            initialFlex: 1,
            minWidth: 400,
            colId: 'name',
        },
        {
            headerName: `${capitalizeFirstLetter(t('price'))} (${getConfigValue(systemConfigKeyEnum.CURRENCY_SYMBOL)})`,
            valueGetter: (params) => (params.data ? (params.data.price ?? params.data.vehicleTypeId?.price) : undefined),
            type: 'numericColumn',
            filter: 'agNumberColumnFilter',
            cellRenderer: (params: ICellRendererParams<UtilityVehicle>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.price}
                        inheritedValue={params.data.vehicleTypeId?.price}
                        format={(value) => formatNumber(value, t('locale'))}
                    />
                ) : (
                    params.value
                ),
            colId: 'price',
        },
        {
            headerName: capitalizeFirstLetter(t('pricing')),
            valueGetter: (params) => (params.data ? t(getPriceTypeInfos(params.data.priceType ?? params.data.vehicleTypeId?.priceType)) : ''),
            cellRenderer: (params: ICellRendererParams<UtilityVehicle>) =>
                params.data ? (
                    <InheritedPriceDisplay
                        directValue={params.data.priceType}
                        inheritedValue={params.data.vehicleTypeId?.priceType}
                        format={(value) => capitalizeFirstLetter(t(getPriceTypeInfos(value)))}
                    />
                ) : (
                    params.value
                ),
            colId: 'pricing',
        },
        {
            headerName: capitalizeFirstLetter(t('codeProC')),
            field: 'codeProC',
            colId: 'codeProC',
        },
        {
            headerName: capitalizeFirstLetter(t('type')),
            field: 'vehicleTypeId.designation',
            colId: 'type',
        },
        {
            headerName: capitalizeFirstLetter(t('plateNumber')),
            field: 'plateNumber',
            cellRenderer: (params: ICellRendererParams<UtilityVehicle>) =>
                params.data ? <LicensePlate number={params.data.plateNumber} /> : params.value,
            colId: 'plateNumber',
        },
        {
            headerName: capitalizeFirstLetter(t('driver')),
            valueGetter: (params) => formatName(params.data?.userId?.firstName, params.data?.userId?.lastName),
            colId: 'driver',
        },
        ...((user?.profileId.permsLevel === permissionLevels.ADMIN
            ? [
                  {
                      headerName: capitalizeFirstLetter(t('_id')),
                      field: '_id',
                      minWidth: 250,
                      colId: '_id',
                      cellClass: 'usage-col-main',
                      headerClass: 'usage-col-main',
                  },
              ]
            : []) as ColDef<UtilityVehicle>[]),
    ];

    return (
        <GenericAgGridTable
            rowData={rowData}
            columnDefs={columnDefs}
            loading={isLoading}
            exportCsvFileName="sales-pli-vehicles"
            storageKey={localStorageKeys.TABLE.PLI.VEHICLES}
            agGridProps={{ getRowId: ({ data }) => data._id }}
        />
    );
};

export default VehiclesPLITable;
